"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import Link from "next/link";
import { Controller, useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useLocale, useTranslations } from "next-intl";
import axios from "axios";
import { Camera, X } from "lucide-react";
import { getRegionLocaleHref } from "@/services/region";
import AvatarImage from "@/components/AvatarImage";
import FormInput from "@/components/FormInput";
import FormSelect from "@/components/FormSelect";
import DateInput from "@/components/DateInput";
import IntlPhoneInput, { type IntlPhoneInputHandle } from "@/components/IntlPhoneInput";
import LottieLoader from "@/components/LottieLoader";
import FormCheckbox from "@/components/FormCheckbox";
import { useRegisterMetadata } from "@/modules/auth/hooks/useRegisterMetadata";
import { useAuth } from "@/modules/auth/context/AuthContext";
import { useCurrentUser } from "@/modules/auth/context/CurrentUserContext";
import { createDependantApi } from "../../services/dependants.api";
import Swal from "sweetalert2";
import Modal from "@/components/Modal";
import { ENGLISH_NAME_REGEX, withEnglishNameFilter } from "@/lib/name";
import { formatDateOnly } from "@/lib/date";

type FormData = {
  fname: string;
  lname: string;
  email: string;
  gender: string;
  birthday: Date | null;
  country: string;
  phone?: string;
  timezone: string;
  terms: boolean;
};

const COUNTRY_TIMEZONE_MAP = {
  EG: "Africa/Cairo",
  SA: "Asia/Riyadh",
} as const;

interface AddDependantModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (newDependant: Record<string, unknown>) => void;
}

export default function AddDependantModal({ isOpen, onClose, onSuccess }: AddDependantModalProps) {
  const auth = useAuth();
  const { refetchUser, user } = useCurrentUser();
  const locale = useLocale();
  const pathname = usePathname();

  // Defaults inherited from the parent account so a family member added from
  // the same household starts pre-filled with the household's country and
  // timezone (the common case). Both remain editable. Primitive strings, so a
  // background user refetch that returns the same values won't reset the form.
  const parentCountry =
    typeof user?.country_code === "string" ? user.country_code : "";
  const parentTimezone =
    typeof user?.timezone === "string" ? user.timezone : "";
  const t = useTranslations("studentSwitchAccount");
  const tRegister = useTranslations("register");
  
  const {
    countries: countryOptions,
    timezones: timezoneOptions,
    loading: metadataLoading,
    errorKey: metadataErrorKey,
  } = useRegisterMetadata();

  const [submitState, setSubmitState] = useState<{
    loading: boolean;
    error: string;
  }>({
    loading: false,
    error: "",
  });
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [avatarError, setAvatarError] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const activeLocale = locale === "ar" ? "ar" : "en";
  const phoneRef = useRef<IntlPhoneInputHandle>(null);

  const schema = z.object({
    fname: z
      .string()
      .trim()
      .min(1, tRegister("required_f_name"))
      .min(3, tRegister("invalid_f_name"))
      .regex(ENGLISH_NAME_REGEX, tRegister("name_english_only")),
    lname: z
      .string()
      .trim()
      .min(1, tRegister("required_l_name"))
      .min(3, tRegister("invalid_l_name"))
      .regex(ENGLISH_NAME_REGEX, tRegister("name_english_only")),
    email: z.string().trim().email(tRegister("invalid_email")).or(z.literal("")),
    gender: z.string().min(1, tRegister("required_gender")),
    birthday: z
      .date({ message: tRegister("required_birth_date") })
      .nullable()
      .refine((val): val is Date => val !== null, {
        message: tRegister("required_birth_date"),
      }),
    country: z.string().min(1, tRegister("required_country")),
    phone: z.string().trim().optional().or(z.literal("")),
    timezone: z.string().min(1, tRegister("required_timezone")),
    terms: z.boolean().refine((v) => v, tRegister("accept_terms")),
  });

  const {
    control,
    register,
    handleSubmit,
    setValue,
    setError,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      fname: "",
      lname: "",
      email: "",
      gender: "",
      birthday: null,
      country: "",
      phone: "",
      timezone: "",
      terms: false,
    },
  });

  // Reset the form each time the modal opens. Country and timezone default to
  // the parent account's values (a family member usually shares the household's
  // country/timezone); both stay editable if the dependant differs.
  useEffect(() => {
    if (isOpen) {
      reset({
        fname: "",
        lname: "",
        email: "",
        gender: "",
        birthday: null,
        country: parentCountry,
        phone: "",
        timezone: parentTimezone,
        terms: false,
      });
      setAvatarFile(null);
      setAvatarPreview(null);
      setAvatarError("");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      setSubmitState({ loading: false, error: "" });
    }
  }, [isOpen, reset, parentCountry, parentTimezone]);

  const genderOptions = useMemo(
    () => [
      { value: "male", label: tRegister("male") },
      { value: "female", label: tRegister("female") },
    ],
    [tRegister],
  );

  const selectedCountry = useWatch({ control, name: "country" });

  // Auto-fill the timezone from the country's canonical zone, but only in
  // response to an explicit country change by the user. This deliberately is
  // NOT a useWatch effect on `country`: seeding `country` from the parent
  // account when the modal opens would otherwise fire it and overwrite the
  // inherited parent timezone. Both entry points (the country select and the
  // phone-number country) route through here.
  const applyCountryTimezone = useCallback(
    (countryCode: string) => {
      const code = countryCode.toUpperCase();
      setValue("country", code);
      if (code in COUNTRY_TIMEZONE_MAP) {
        setValue("timezone", COUNTRY_TIMEZONE_MAP[code as keyof typeof COUNTRY_TIMEZONE_MAP]);
      }
    },
    [setValue],
  );

  const handleAvatarChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validTypes = ["image/jpeg", "image/png", "image/webp"];
    if (!validTypes.includes(file.type)) {
      setAvatarError(t("invalid_image_type"));
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setAvatarError(t("image_too_large"));
      return;
    }

    setAvatarError("");
    setAvatarFile(file);

    const reader = new FileReader();
    reader.onloadend = () => {
      setAvatarPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveAvatar = () => {
    setAvatarFile(null);
    setAvatarPreview(null);
    setAvatarError("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const onSubmit = async (data: FormData) => {
    if (!phoneRef.current?.isValid()) {
      setError("phone", { type: "manual", message: tRegister("invalid_phone") });
      return;
    }

    setSubmitState({ loading: true, error: "" });

    const birthdayString = data.birthday ? formatDateOnly(data.birthday) : null;

    const payload = {
      fname: data.fname,
      lname: data.lname,
      email: data.email,
      gender: data.gender,
      birthday: birthdayString,
      country: data.country,
      phone: data.phone,
      timezone: data.timezone,
      image: avatarFile,
      terms: data.terms,
    };

    try {
      const response = await createDependantApi(payload);

      if (auth) {
        // AuthContext is mounted (e.g. dashboard pages) - update its cached
        // user directly; this also notifies CurrentUserContext to refetch.
        auth.replaceUser(response.user);
      } else {
        // AuthContext isn't mounted on this route (e.g. booking pages) -
        // refetch CurrentUserContext's user directly so the dependants
        // list (and the attendee selector built from it) picks up the
        // newly created dependant without a full page reload.
        await refetchUser();
      }

      Swal.fire({
        title: t("create_success"),
        icon: "success",
        toast: true,
        position: locale === "ar" ? "top-start" : "top-end",
        showConfirmButton: false,
        timer: 3000,
      });

      if (onSuccess) {
        onSuccess(response.dependant);
      }
      onClose();
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        const message =
          typeof error.response?.data?.message === "string"
            ? error.response.data.message
            : extractFirstValidationError(error.response?.data?.errors) ?? t("submission_failed");

        setSubmitState({
          loading: false,
          error: message,
        });
        return;
      }

      setSubmitState({
        loading: false,
        error: t("submission_failed"),
      });
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      overlayClassName="fixed inset-0 z-50 flex items-center justify-center bg-backdrop p-4 overflow-y-auto h-screen w-screen"
      closeOnBackdropClick={false}
      closeOnEscape={false}
      trapFocus={false}
    >
      <div className="relative w-full max-w-2xl rounded-[10px] bg-modal p-6 md:p-8 shadow-2xl max-h-[90vh] overflow-y-auto">

        {/* Close Button */}
        <button
          onClick={onClose}
          type="button"
          className="absolute right-4 top-4 rounded-[10px] border border-border p-2 text-text-muted hover:bg-hover hover:text-primary transition cursor-pointer"
        >
          <X size={20} />
        </button>

        {/* Modal Header */}
        <div className="mb-6">
          <h2 className="text-2xl font-semibold text-primary">{t("create_dependant")}</h2>
          <p className="mt-2 text-sm text-text-secondary">{t("create_description")}</p>
        </div>

        {/* Error Alert Box */}
        {submitState.error ? (
          <div className="mb-4 rounded-[10px] bg-danger/10 p-3 text-sm text-danger">{submitState.error}</div>
        ) : null}
        {metadataErrorKey ? (
          <div className="mb-4 rounded-[10px] bg-danger/10 p-3 text-sm text-danger">
            {tRegister(metadataErrorKey)}
          </div>
        ) : null}

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-12 gap-4">
          <div className="col-span-12 mb-2 flex flex-col items-center gap-3 border-b border-border-subtle pb-6">
            <div className="relative group">
              <div className="relative h-24 w-24 overflow-hidden rounded-full border-4 border-card shadow-md ring-1 ring-border">
                <AvatarImage
                  src={avatarPreview}
                  alt={t("create_dependant")}
                  fill
                  className="object-cover"
                />
              </div>

              <label className="absolute inset-0 flex cursor-pointer flex-col items-center justify-center rounded-full bg-black/40 opacity-0 transition-opacity group-hover:opacity-100">
                <Camera className="mb-1 text-white" size={20} />
                <span className="text-[9px] font-bold uppercase tracking-wider text-white">
                  {t("change_avatar")}
                </span>
                <input
                  type="file"
                  className="hidden"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleAvatarChange}
                  ref={fileInputRef}
                />
              </label>

              {avatarPreview && (
                <button
                  type="button"
                  onClick={handleRemoveAvatar}
                  className="absolute -right-1 -top-1 flex h-7 w-7 items-center justify-center rounded-full bg-card text-danger shadow-md ring-1 ring-border-subtle hover:bg-danger/10 transition cursor-pointer"
                  title={t("remove_avatar")}
                >
                  <X size={16} />
                </button>
              )}
            </div>

            {avatarError ? (
              <p className="text-xs font-medium text-danger">{avatarError}</p>
            ) : (
              <p className="text-xs text-text-muted">{t("avatar_hint")}</p>
            )}
          </div>

          <div className="col-span-12 md:col-span-6">
            <FormInput
              label={tRegister("first_name")}
              placeholder={tRegister("enter_first_name")}
              error={errors.fname?.message}
              register={withEnglishNameFilter(register("fname"))}
            />
          </div>

          <div className="col-span-12 md:col-span-6">
            <FormInput
              label={tRegister("last_name")}
              placeholder={tRegister("enter_last_name")}
              error={errors.lname?.message}
              register={withEnglishNameFilter(register("lname"))}
            />
          </div>

          <div className="col-span-12 md:col-span-6 z-30">
            <Controller
              name="gender"
              control={control}
              render={({ field }) => (
                <FormSelect
                  label={tRegister("gender")}
                  options={genderOptions}
                  placeholder={tRegister("select_gender")}
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.gender?.message}
                />
              )}
            />
          </div>

          <div className="col-span-12 md:col-span-6">
            <label className="mb-1.5 block text-sm text-foreground">{tRegister("birth_date")}</label>
            <Controller
              name="birthday"
              control={control}
              render={({ field }) => (
                <DateInput
                  onChange={field.onChange}
                  locale={activeLocale}
                  maxDate={new Date()}
                  placeholder={tRegister("enter_birth_date")}
                  value={field.value ?? undefined}
                />
              )}
            />
            {errors.birthday?.message ? (
              <p className="mt-1 text-xs text-danger">{errors.birthday.message}</p>
            ) : null}
          </div>

          <div className="col-span-12 md:col-span-6 z-20">
            <Controller
              name="country"
              control={control}
              render={({ field }) => (
                <FormSelect
                  label={tRegister("country")}
                  options={countryOptions}
                  placeholder={tRegister("select_country")}
                  value={field.value}
                  onChange={applyCountryTimezone}
                  error={errors.country?.message}
                  isLoading={metadataLoading}
                  isDisabled={metadataLoading}
                />
              )}
            />
          </div>

          <div className="col-span-12 md:col-span-6">
            <Controller
              name="phone"
              control={control}
              render={({ field }) => (
                <IntlPhoneInput
                  ref={phoneRef}
                  label={`${tRegister("phone")} (${t("optional")})`}
                  locale={activeLocale}
                  country={selectedCountry}
                  value={field.value ?? ""}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  onCountryChange={applyCountryTimezone}
                  error={errors.phone?.message}
                />
              )}
            />
          </div>

          <div className="col-span-12 md:col-span-6 z-10">
            <Controller
              name="timezone"
              control={control}
              render={({ field }) => (
                <FormSelect
                  label={tRegister("timezone")}
                  options={timezoneOptions}
                  placeholder={tRegister("select_timezone")}
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.timezone?.message}
                  isLoading={metadataLoading}
                  isDisabled={metadataLoading}
                />
              )}
            />
          </div>

          <div className="col-span-12 flex items-center gap-2 pt-2 border-t border-border-subtle mt-2">
            <FormCheckbox
              label={tRegister.rich("terms", {
                terms: (chunks) => (
                  <Link
                    href={getRegionLocaleHref(pathname, locale, "/terms-of-service")}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(event) => event.stopPropagation()}
                    className="underline underline-offset-2 hover:opacity-80"
                  >
                    {chunks}
                  </Link>
                ),
              })}
              register={register("terms")}
              error={errors.terms?.message}
            />
          </div>

          <div className="col-span-12 flex flex-wrap gap-3 justify-end">
            <button
              onClick={onClose}
              type="button"
              className="rounded-[10px] border border-border px-6 py-3 font-semibold text-foreground transition hover:bg-hover cursor-pointer"
            >
              {t("cancel")}
            </button>
            <button
              type="submit"
              disabled={submitState.loading || metadataLoading}
              className="rounded-[10px] bg-primary px-6 py-3 font-semibold text-on-primary transition hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer"
            >
              {submitState.loading ? (
                <LottieLoader centered size={18} label={t("saving")} />
              ) : (
                t("save_dependant")
              )}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
}

function extractFirstValidationError(errors: unknown): string | null {
  if (typeof errors !== "object" || errors === null) {
    return null;
  }

  for (const value of Object.values(errors as Record<string, unknown>)) {
    if (Array.isArray(value) && typeof value[0] === "string") {
      return value[0];
    }
  }

  return null;
}
